import React, { useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { COPY } from '../presentationCopy';
import { PRES, TYPE } from '../chrome';

export type PresentationAiChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

interface PresentationAiPanelProps {
  enabled: boolean;
  busy?: boolean;
  hasSelectedText?: boolean;
  onAsk: (instruction: string) => Promise<boolean>;
  chatMessages?: PresentationAiChatMessage[];
  onChat?: (message: string) => Promise<boolean>;
  onInsertChat?: (content: string) => boolean;
  onClearChat?: () => void;
}

const PresentationAiPanel: React.FC<PresentationAiPanelProps> = ({
  enabled,
  busy,
  hasSelectedText,
  onAsk,
  chatMessages = [],
  onChat,
  onInsertChat,
  onClearChat,
}) => {
  const [tab, setTab] = useState<'box' | 'chat'>('box');
  const [instruction, setInstruction] = useState('');
  const [chatInput, setChatInput] = useState('');

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0, height: '100%' }}>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.title, color: PRES.textPrimary }}>
        {COPY.aiChat}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {(['box', 'chat'] as const).map((id) => (
          <Button
            key={id}
            size="small"
            onClick={() => setTab(id)}
            sx={{
              textTransform: 'none',
              fontWeight: tab === id ? 650 : 500,
              bgcolor: tab === id ? PRES.navy : PRES.cream,
              color: tab === id ? PRES.white : PRES.textPrimary,
              '&:hover': { bgcolor: tab === id ? PRES.navyDeep : PRES.line },
            }}
          >
            {id === 'box' ? COPY.aiTabBox : COPY.aiTabChat}
          </Button>
        ))}
      </Box>
      {!enabled && (
        <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
          {COPY.aiUnavailable}
        </Typography>
      )}
      {enabled && tab === 'box' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.aiChatHelp}
          </Typography>
          <TextField
            size="small"
            multiline
            minRows={3}
            value={instruction}
            onChange={(event) => setInstruction(event.target.value)}
            placeholder={COPY.aiChatPlaceholder}
          />
          <Button
            disabled={!instruction.trim() || busy}
            onClick={async () => {
              const ok = await onAsk(instruction.trim());
              if (ok) setInstruction('');
            }}
            sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
          >
            {COPY.aiRewrite}
          </Button>
        </Box>
      )}
      {enabled && tab === 'chat' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0, flex: 1 }}>
          <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.body, color: PRES.textSecondary }}>
            {COPY.aiChatThreadHelp}
          </Typography>
          <Box sx={{
            flex: 1,
            minHeight: 180,
            maxHeight: 360,
            overflow: 'auto',
            border: `1px solid ${PRES.line}`,
            borderRadius: 1,
            p: 1,
            bgcolor: PRES.cream,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
            {!chatMessages.length && (
              <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.muted }}>
                {COPY.aiChatEmpty}
              </Typography>
            )}
            {chatMessages.map((item, index) => (
              <Box
                key={`${item.role}-${index}`}
                sx={{
                  alignSelf: item.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '92%',
                  px: 1,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: item.role === 'user' ? PRES.navy : PRES.white,
                  color: item.role === 'user' ? PRES.white : PRES.textPrimary,
                  whiteSpace: 'pre-wrap',
                  fontFamily: TYPE.ui,
                  ...TYPE.body,
                }}
              >
                {item.content}
                {item.role === 'assistant' && onInsertChat && (
                  <Button
                    size="small"
                    disabled={!hasSelectedText || busy}
                    onClick={() => onInsertChat(item.content)}
                    sx={{
                      display: 'block',
                      mt: 0.75,
                      textTransform: 'none',
                      color: PRES.navy,
                      minWidth: 0,
                      px: 0,
                    }}
                  >
                    {COPY.aiChatInsert}
                  </Button>
                )}
              </Box>
            ))}
          </Box>
          <TextField
            size="small"
            multiline
            minRows={2}
            value={chatInput}
            onChange={(event) => setChatInput(event.target.value)}
            placeholder={COPY.aiChatInput}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && chatInput.trim() && onChat && !busy) {
                event.preventDefault();
                const message = chatInput.trim();
                void onChat(message).then((ok) => {
                  if (ok) setChatInput('');
                });
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              disabled={!chatInput.trim() || busy || !onChat}
              onClick={async () => {
                if (!onChat) return;
                const ok = await onChat(chatInput.trim());
                if (ok) setChatInput('');
              }}
              sx={{ textTransform: 'none' }}
            >
              {busy ? COPY.aiRewriteBusy : COPY.aiChatSend}
            </Button>
            {Boolean(chatMessages.length) && onClearChat && (
              <Button disabled={busy} onClick={onClearChat} sx={{ textTransform: 'none', color: PRES.muted }}>
                {COPY.aiChatClear}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PresentationAiPanel;
